/**
 * Ansprechpartner eines KIAN-Ticketentwurfs: einer, den der Kunde schon hat, ein neu angelegter
 * oder bewusst keiner.
 *
 * Bis v1.388.x war das ein Freitextfeld. KIAN schrieb dort einen Namen hinein, den erst der Server
 * beim Auflösen einem Kontakt zuordnete — oder eben nicht, und das Ticket entstand dann still ohne
 * Ansprechpartner. Die Liste kommt wie auf „Neues Ticket" aus `/weclapp/parties/:id/contacts`,
 * „Neu anlegen" ist derselbe `ContactModal`.
 */
import { useMemo, useState } from 'react';
import { ActionIcon, Group, Loader, Select, Text, Tooltip } from '@mantine/core';
import { IconUserPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { ContactModal } from '../../ContactModal';

/** Auswahl „ohne Ansprechpartner" — eigener Wert, damit „keiner" und „noch offen" verschieden aussehen. */
export const NO_CONTACT = '__none__';

export interface DraftContactState {
  contactId?: string | null;
  contact?: string | null;
}

/**
 * Wert des Felds: gewählter Kontakt, sonst der vom Server zugeordnete, sonst — ohne jede Angabe —
 * „ohne Ansprechpartner". Ein Name aus dem Entwurf, der sich nicht zuordnen ließ, ist `null`
 * (Feld leer, Hinweis darunter): Er wird ohne Auswahl NICHT zum Ansprechpartner.
 */
export function draftContactValue(draft: DraftContactState, resolvedContactId: string | null | undefined): string | null {
  if (draft.contactId) return draft.contactId;
  if (draft.contact) return resolvedContactId ?? null;
  return NO_CONTACT;
}

/** Änderung für den Entwurf aus einer Auswahl im Feld. */
export function draftContactChange(value: string | null, label: string | null): Required<DraftContactState> {
  if (!value || value === NO_CONTACT) return { contactId: null, contact: null };
  return { contactId: value, contact: label };
}

interface Props {
  /** weclapp-Party-ID des gewählten Kunden */
  partyId: string | null;
  draft: DraftContactState;
  resolvedContact: { id: string; name: string } | null | undefined;
  onChange: (next: Required<DraftContactState>) => void;
  /** über dem Entwurfsdialog (5300) */
  zIndex: number;
}

export function KianDraftContactField({ partyId, draft, resolvedContact, onChange, zIndex }: Props) {
  const [creating, setCreating] = useState(false);
  const contactsQuery = useQuery({
    queryKey: ['weclapp-contacts', partyId],
    queryFn: () => api.getWeclappContactsByParty(partyId!),
    enabled: !!partyId,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const contacts = contactsQuery.data?.contacts ?? [];

  const value = draftContactValue(draft, resolvedContact?.id);
  const options = useMemo(() => {
    const opts = contacts
      .slice()
      .sort((a, b) => Number(b.isPrimary ?? false) - Number(a.isPrimary ?? false))
      .map((c) => ({
        value: String(c.id),
        label: `${c.isPrimary ? '★ ' : ''}${c.email ? `${c.name} (${c.email})` : c.name}`,
        name: c.name,
      }));
    // Gerade angelegt, Liste noch nicht neu geladen: trotzdem anzeigen
    if (value && value !== NO_CONTACT && !opts.some((o) => o.value === value)) {
      const name = draft.contact ?? resolvedContact?.name ?? value;
      opts.unshift({ value, label: name, name });
    }
    return [{ value: NO_CONTACT, label: 'Ohne Ansprechpartner', name: '' }, ...opts];
  }, [contacts, value, draft.contact, resolvedContact]);

  const unmatched = !!draft.contact && !draft.contactId && !resolvedContact;

  return (
    <div>
      <Text size="sm" fw={500} mb={4}>
        Ansprechpartner
      </Text>
      <Group gap="xs" align="flex-start" wrap="nowrap">
        <div style={{ flex: 1, minWidth: 0 }}>
          {contactsQuery.isLoading ? (
            <Group gap="xs" align="center" h={36}>
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                Ansprechpartner werden geladen…
              </Text>
            </Group>
          ) : (
            <Select
              placeholder={!partyId ? 'Zuerst Kunden auswählen' : 'Ansprechpartner wählen'}
              data={options}
              value={value}
              onChange={(v) => onChange(draftContactChange(v, options.find((o) => o.value === v)?.name ?? null))}
              disabled={!partyId}
              searchable
              nothingFoundMessage="Keine Kontakte gefunden"
              error={contactsQuery.isError ? 'Ansprechpartner nicht ladbar — „Ohne" oder später erneut öffnen' : undefined}
              description={
                unmatched
                  ? `Aus dem Entwurf: „${draft.contact}" — beim Kunden nicht gefunden. Bitte auswählen, neu anlegen oder „Ohne".`
                  : undefined
              }
              comboboxProps={{ zIndex: zIndex + 100 }}
            />
          )}
        </div>
        {partyId && !contactsQuery.isLoading ? (
          <Tooltip label="Neuen Ansprechpartner anlegen" zIndex={zIndex + 100}>
            <ActionIcon variant="default" size="lg" onClick={() => setCreating(true)} aria-label="Neuen Ansprechpartner anlegen">
              <IconUserPlus size={16} />
            </ActionIcon>
          </Tooltip>
        ) : null}
      </Group>

      {partyId && creating ? (
        <ContactModal
          opened={creating}
          onClose={() => setCreating(false)}
          mode={{ system: 'weclapp', partyId, prefillName: unmatched ? draft.contact ?? undefined : undefined }}
          zIndex={zIndex + 100}
          onSaved={(c) => onChange({ contactId: String(c.id), contact: c.name })}
        />
      ) : null}
    </div>
  );
}
